import React, { useRef, useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  ReturnArrowIcon, SonicWaveIcon, SacredGearIcon, RegenerateCycleIcon
} from './CustomIcons';
import PersistentFooter from './PersistentFooter';

const SonicExperience = ({ player, onNavigateToGame, onNavigateHome }) => {
  const canvasRef = useRef(null);
  const animationRef = useRef(null);
  const audioEngineRef = useRef(null);
  const [isRunning, setIsRunning] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(false);
  
  // Grant's 7-Dimensional Variables - Lower frequencies as default for gentler experience
  const [dimensions, setDimensions] = useState({
    r: 1.618, // Radial distance (Golden ratio)
    theta: 0, // Theta angle (azimuthal)
    phi: 0, // Phi angle (polar)
    t: 1.0, // Time dimension
    f: 147, // Base frequency (lowered by 1/3 from 220 to 147 for default gentle tone)
    u: 1.0, // Universal constant
    infinity: 3.14159, // Infinity constant (π)
    tuvanThroat: 43 // Default deeper throat singing (lowered by 1/3 from 65 to 43)
  });
  
  const [machineState, setMachineState] = useState({
    power: 0,
    resonance: 0,
    steamPressure: 0,
    energyOutput: 0,
    discoveredFormulas: [],
    dimensionalHarmony: 0,
    evolutionLevel: 1
  });

  // Grant's Mathematical Formulas
  const grantFormulas = [
    {
      id: 'dynamic_frequency',
      name: '5-D Dynamic Frequency',
      formula: 'F₅ = f⋅sin(θ)⋅cos(φ)⋅r^(t/u)',
      variables: ['f', 'theta', 'phi', 'r', 't', 'u'],
      unlock: { f: 528, theta: 45, phi: 30, r: 1.618, t: 1.5, u: 1.0 },
      tolerance: 0.1,
      description: 'Core frequency that bridges dimensions',
      energyBonus: 500
    },
    {
      id: 'universal_framework',
      name: '7-D Universal Framework',
      formula: 'U₇ = ∑(r^n⋅sin(nθ+φ)⋅f^(t/infinity))',
      variables: ['r', 'theta', 'phi', 'f', 't', 'infinity'],
      unlock: { r: 3.14159, theta: 216, phi: 108, f: 741, t: 2.618, infinity: 3.14159 },
      tolerance: 0.08,
      description: 'Complete universal dimensional integration',
      energyBonus: 1000
    }
  ];

  // Initialize Cosmic Audio Engine
  useEffect(() => {
    audioEngineRef.current = new CosmicAudioEngine();
    return () => {
      if (audioEngineRef.current) {
        audioEngineRef.current.destroy();
      }
    };
  }, []);

  // Main Engine Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    
    let time = 0;

    const drawGrantsSteampunkEngine = () => {
      // Clear with cosmic background
      ctx.fillStyle = 'rgba(0, 0, 20, 0.1)';
      ctx.fillRect(0, 0, width, height);

      // Central computational core
      const coreSize = 60 + Math.sin(time * 0.02) * 8;
      const coreGradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, coreSize);
      coreGradient.addColorStop(0, `rgba(255, 215, 0, ${machineState.power / 100})`);
      coreGradient.addColorStop(0.4, `rgba(255, 140, 0, ${machineState.power / 150})`);
      coreGradient.addColorStop(1, 'rgba(139, 69, 19, 0.3)');
      
      ctx.fillStyle = coreGradient;
      ctx.beginPath();
      ctx.arc(centerX, centerY, coreSize, 0, 2 * Math.PI);
      ctx.fill();

      // R - Radial gears (rotating cosmic gears)
      const numGears = 6;
      for (let i = 0; i < numGears; i++) {
        const gearAngle = time * 0.03 * dimensions.r + (i * 2 * Math.PI) / numGears;
        const gearRadius = 100 + dimensions.r * 12;
        const gearX = centerX + gearRadius * Math.cos(gearAngle);
        const gearY = centerY + gearRadius * Math.sin(gearAngle);
        
        // Cosmic gear body
        ctx.fillStyle = `rgba(138, 43, 226, ${machineState.power / 120})`;
        ctx.beginPath();
        ctx.arc(gearX, gearY, 20, 0, 2 * Math.PI);
        ctx.fill();
        
        // Gear teeth
        ctx.fillStyle = `rgba(75, 0, 130, ${machineState.power / 100})`;
        for (let j = 0; j < 8; j++) {
          const toothAngle = gearAngle + (j * Math.PI) / 4;
          const toothX = gearX + 25 * Math.cos(toothAngle);
          const toothY = gearY + 25 * Math.sin(toothAngle);
          ctx.beginPath();
          ctx.arc(toothX, toothY, 4, 0, 2 * Math.PI);
          ctx.fill();
        }
      }

      // Energy lightning effects
      if (machineState.energyOutput > 50) {
        for (let i = 0; i < 10; i++) {
          const lightningAngle = Math.random() * Math.PI * 2;
          const lightningR = 40 + Math.random() * 80;
          const lightningX = centerX + lightningR * Math.cos(lightningAngle);
          const lightningY = centerY + lightningR * Math.sin(lightningAngle);
          
          ctx.strokeStyle = `rgba(0, 255, 255, ${Math.random() * 0.8})`;
          ctx.lineWidth = Math.random() * 3 + 1;
          ctx.beginPath();
          ctx.moveTo(centerX, centerY);
          ctx.lineTo(lightningX, lightningY);
          ctx.stroke();
        }
      }
      
      time += 1;
    };

    const animate = () => {
      if (isRunning) {
        drawGrantsSteampunkEngine();
        
        // Calculate machine state from dimensional parameters
        const powerFromDimensions = Math.min(100, 
          (dimensions.r * 20) + 
          (Math.sin(dimensions.theta * Math.PI / 180) * 30) + 
          (dimensions.f / 20)
        );
        
        const energyFromFormula = (powerFromDimensions * dimensions.u) / 2;
        
        setMachineState(prev => ({
          ...prev,
          power: powerFromDimensions,
          energyOutput: Math.abs(energyFromFormula)
        }));
        
        animationRef.current = requestAnimationFrame(animate);
      } else {
        drawGrantsSteampunkEngine();
      }
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [dimensions, isRunning, machineState.power]);

  const updateDimension = (dim, value) => {
    const newDimensions = { ...dimensions, [dim]: value };
    setDimensions(newDimensions);
    
    // Update audio system
    if (audioEngineRef.current && audioEnabled) {
      audioEngineRef.current.updateDimensions(newDimensions);
    }
  };

  const toggleAudio = async () => {
    if (!audioEngineRef.current) return;

    if (!audioEnabled) {
      const success = await audioEngineRef.current.startCosmicSounds();
      if (success) {
        setAudioEnabled(true);
        
        // AUTO-ENABLE PERSISTENCE for seamless experience
        if (audioEngineRef.current) {
          audioEngineRef.current.enablePersistentMode();
          console.log('🎵 AUTO-ENABLED: Sonic persistence for seamless experience');
        }
        
        audioEngineRef.current.updateDimensions(dimensions);
      }
    } else {
      audioEngineRef.current.stopCosmicSounds();
      setAudioEnabled(false);
    }
  };

  const startEngine = () => {
    setIsRunning(true);
    if (audioEnabled && audioEngineRef.current) {
      audioEngineRef.current.updateDimensions(dimensions);
    }
  };

  const stopEngine = () => {
    setIsRunning(false);
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden"
      style={{
        backgroundImage: `url(https://customer-assets.emergentagent.com/job_galactic-spiral/artifacts/3g4co2av_6633B3C2-7ABE-477D-9177-B3A85A253037.jpeg)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center'
      }}
    >
      {/* Cosmic overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-purple-900/30 to-black/50"></div>

      <div className="relative z-10 max-w-7xl mx-auto p-4">
        
        {/* Header */}
        <div className="flex items-center justify-between mb-6 pt-4">
          <Button
            onClick={onNavigateToGame}
            variant="outline"
            className="text-white border-white/60 hover:bg-white/10 backdrop-blur-xl"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Game
          </Button>
          
          {/* Grant's Book Link */}
          <a
            href="https://www.amazon.com/dp/B0DMKRRDWR?ref=cm_sw_r_ffobk_cp_ud_dp_2P9RPVA7S9MD53GAJ9J8"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-yellow-600/80 to-orange-600/80 hover:from-yellow-600 hover:to-orange-600 text-white px-4 py-2 rounded-xl backdrop-blur-xl border border-yellow-500/30 transition-all flex items-center space-x-2"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            <Crown className="w-4 h-4" />
            <span className="text-sm">Grant's Book</span>
          </a>
          
          <h1 
            className="text-2xl md:text-4xl font-bold text-white text-center"
            style={{ 
              fontFamily: '"Crimson Text", "Libre Baskerville", serif',
              textShadow: '0 0 20px rgba(255, 255, 255, 0.8)'
            }}
          >
            Grant's Sonic Dimension
          </h1>

          <Button
            onClick={onNavigateHome}
            variant="outline"
            className="text-white border-white/60 hover:bg-white/10 backdrop-blur-xl"
            style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
          >
            <Home className="w-4 h-4 mr-2" />
            Home
          </Button>
        </div>

        {/* Engine Header */}
        <div className="bg-black/60 backdrop-blur-xl border border-purple-600/50 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className={`w-10 h-10 text-purple-400 ${isRunning ? 'animate-spin' : ''}`}>⚙️</div>
                <div className="absolute -top-2 -right-1 w-6 h-6 text-cyan-400 animate-pulse">🌌</div>
              </div>
              <div>
                <h2 
                  className="text-3xl font-bold text-white"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  Grant's Dimensional Engine
                </h2>
                <p className="text-purple-300">7-D Mathematical Sonic Framework</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <Badge className={`${
                machineState.power > 80 ? 'bg-green-600' :
                machineState.power > 50 ? 'bg-yellow-600' : 'bg-red-600'
              } text-white`}>
                ⚡ {Math.floor(machineState.power)}% Power
              </Badge>
              
              <Badge className="bg-purple-600 text-white">
                🌌 Evolution Lv.{machineState.evolutionLevel}
              </Badge>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          
          {/* Main Engine Visualization */}
          <div className="lg:col-span-2">
            <Card className="bg-black/40 backdrop-blur-xl border border-purple-600/40 shadow-2xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle 
                    className="text-white flex items-center space-x-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    <span className="text-6xl">🌌</span>
                    <span>Dimensional Computation Matrix</span>
                  </CardTitle>
                  <div className="flex items-center space-x-3">
                    <Button
                      onClick={isRunning ? stopEngine : startEngine}
                      className={isRunning 
                        ? 'bg-red-600/30 border border-red-500/50 text-red-300 hover:bg-red-600/40' 
                        : 'bg-green-600/30 border border-green-500/50 text-green-300 hover:bg-green-600/40'
                      }
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      {isRunning ? <Pause className="w-4 h-4 mr-2" /> : <Play className="w-4 h-4 mr-2" />}
                      {isRunning ? 'STOP' : 'START'}
                    </Button>
                    
                    <Button
                      onClick={toggleAudio}
                      className={audioEnabled
                        ? 'bg-purple-600/30 border border-purple-500/50 text-purple-300'
                        : 'bg-gray-600/30 border border-gray-500/50 text-gray-300'
                      }
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      {audioEnabled ? <Volume2 className="w-4 h-4 mr-2" /> : <VolumeX className="w-4 h-4 mr-2" />}
                      {audioEnabled ? 'ON' : 'OFF'}
                    </Button>
                    
                    <Button
                      onClick={() => {
                        if (audioEngineRef.current) {
                          if (audioEngineRef.current.persistentMode) {
                            audioEngineRef.current.disablePersistentMode();
                          } else {
                            audioEngineRef.current.enablePersistentMode();
                          }
                        }
                      }}
                      className={audioEngineRef.current?.persistentMode
                        ? 'bg-cyan-600/30 border border-cyan-500/50 text-cyan-300'
                        : 'bg-gray-600/30 border border-gray-500/50 text-gray-300'
                      }
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      <Zap className="w-4 h-4 mr-2" />
                      {audioEngineRef.current?.persistentMode ? 'PERSIST' : 'LOCAL'}
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="p-6">
                {/* Main Canvas */}
                <div className="relative mb-6">
                  <canvas
                    ref={canvasRef}
                    width={600}
                    height={400}
                    className="w-full h-auto rounded-xl border-4 border-purple-600/40 shadow-2xl cursor-pointer"
                    style={{
                      background: 'radial-gradient(circle, rgba(25,25,112,0.98) 0%, rgba(72,61,139,0.95) 50%, rgba(25,25,25,1) 100%)'
                    }}
                    onClick={() => {
                      if (audioEnabled && machineState.power > 30) {
                        // Play discovery chime
                        if (audioEngineRef.current) {
                          audioEngineRef.current.playFormulaDiscoverySound('Manual Discovery');
                        }
                      }
                    }}
                  />
                  
                  {/* Cosmic Gauges */}
                  <div className="absolute top-4 left-4 grid grid-cols-2 gap-3">
                    <div className="bg-black/80 backdrop-blur-sm rounded-lg p-3 border-2 border-purple-600/50">
                      <div className="text-purple-300 text-xs font-bold mb-1">⚡ POWER</div>
                      <div className="text-green-400 text-lg font-mono">{Math.floor(machineState.power)}%</div>
                    </div>
                    <div className="bg-black/80 backdrop-blur-sm rounded-lg p-3 border-2 border-purple-600/50">
                      <div className="text-purple-300 text-xs font-bold mb-1">🌊 RESONANCE</div>
                      <div className="text-cyan-400 text-lg font-mono">{Math.floor(machineState.resonance)}%</div>
                    </div>
                  </div>
                </div>

                {/* Engine Status Display */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-purple-900/30 rounded-lg border border-purple-600/40">
                    <div className="text-4xl mb-2">🔥</div>
                    <div className="text-xl font-bold text-white">{Math.floor(machineState.power)}%</div>
                    <div className="text-xs text-purple-300">Engine Power</div>
                  </div>
                  <div className="text-center p-4 bg-cyan-900/30 rounded-lg border border-cyan-600/40">
                    <div className="text-4xl mb-2">📈</div>
                    <div className="text-xl font-bold text-white">{Math.floor(machineState.energyOutput)}</div>
                    <div className="text-xs text-cyan-300">Energy Rate</div>
                  </div>
                  <div className="text-center p-4 bg-yellow-900/30 rounded-lg border border-yellow-600/40">
                    <div className="text-4xl mb-2">🎵</div>
                    <div className="text-xl font-bold text-white">{Math.floor(dimensions.f)} Hz</div>
                    <div className="text-xs text-yellow-300">Frequency</div>
                  </div>
                  <div className="text-center p-4 bg-green-900/30 rounded-lg border border-green-600/40">
                    <div className="text-4xl mb-2">✨</div>
                    <div className="text-xl font-bold text-white">{machineState.discoveredFormulas.length}</div>
                    <div className="text-xs text-green-300">Formulas</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Dimensional Control Panel */}
          <div className="space-y-6">
            
            {/* Grant's 7-D Controls */}
            <Card className="bg-black/40 backdrop-blur-xl border border-purple-600/40 shadow-xl">
              <CardHeader>
                <CardTitle 
                  className="text-white flex items-center space-x-2"
                  style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                >
                  <Settings className="w-6 h-6 text-purple-400" />
                  <span>Dimensional Controls</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                
                {/* Sacred Frequency */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      🎵 F - Sacred Frequency
                    </label>
                    <Badge className="bg-cyan-600 text-white">
                      {Math.floor(dimensions.f)} Hz
                    </Badge>
                  </div>
                  <input
                    type="range"
                    value={dimensions.f}
                    onChange={(e) => updateDimension('f', parseFloat(e.target.value))}
                    min={40}
                    max={1000}
                    step={1}
                    className="w-full h-6 accent-cyan-400"
                  />
                  <div className="flex justify-between text-xs text-cyan-400 mt-1">
                    <span>40 Hz</span>
                    <span>528 Hz (Love)</span>
                    <span>1000 Hz</span>
                  </div>
                </div>

                {/* Radial Distance */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      ⚙️ R - Radial Distance
                    </label>
                    <Badge className="bg-yellow-600 text-white">
                      {dimensions.r.toFixed(3)}
                    </Badge>
                  </div>
                  <input
                    type="range"
                    value={dimensions.r}
                    onChange={(e) => updateDimension('r', parseFloat(e.target.value))}
                    min={0.1}
                    max={5.0}
                    step={0.001}
                    className="w-full h-6 accent-yellow-400"
                  />
                  <div className="flex justify-between text-xs text-yellow-400 mt-1">
                    <span>0.1</span>
                    <span>φ = 1.618</span>
                    <span>5.0</span>
                  </div>
                </div>

                {/* Theta Angle */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      🎯 θ - Theta Angle
                    </label>
                    <Badge className="bg-red-600 text-white">
                      {Math.floor(dimensions.theta)}°
                    </Badge>
                  </div>
                  <input
                    type="range"
                    value={dimensions.theta}
                    onChange={(e) => updateDimension('theta', parseFloat(e.target.value))}
                    min={0}
                    max={360}
                    step={1}
                    className="w-full h-6 accent-red-400"
                  />
                </div>

                {/* Time Flow */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      📊 T - Time Flow
                    </label>
                    <Badge className="bg-blue-600 text-white">
                      {dimensions.t.toFixed(2)}x
                    </Badge>
                  </div>
                  <input
                    type="range"
                    value={dimensions.t}
                    onChange={(e) => updateDimension('t', parseFloat(e.target.value))}
                    min={0.1}
                    max={5.0}
                    step={0.01}
                    className="w-full h-6 accent-blue-400"
                  />
                </div>

                {/* Universal Field */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      👑 U - Universal Field
                    </label>
                    <Badge className="bg-green-600 text-white">
                      {dimensions.u.toFixed(3)}
                    </Badge>
                  </div>
                  <input
                    type="range"
                    value={dimensions.u}
                    onChange={(e) => updateDimension('u', parseFloat(e.target.value))}
                    min={0.1}
                    max={3.0}
                    step={0.001}
                    className="w-full h-6 accent-green-400"
                  />
                </div>

                {/* NEW: Tuvan Throat Singing */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label 
                      className="text-sm font-medium text-white"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      🎵 Tuvan Throat Singing
                    </label>
                    <Badge className="bg-orange-600 text-white">
                      {Math.floor(dimensions.tuvanThroat)} Hz
                    </Badge>
                  </div>
                  <input
                    type="range"
                    value={dimensions.tuvanThroat}
                    onChange={(e) => updateDimension('tuvanThroat', parseFloat(e.target.value))}
                    min={60}
                    max={120}
                    step={1}
                    className="w-full h-6 accent-orange-400"
                  />
                  <div className="flex justify-between text-xs text-orange-400 mt-1">
                    <span>Deep</span>
                    <span>Traditional</span>
                    <span>High</span>
                  </div>
                </div>

                {/* Sacred Presets */}
                <div className="pt-4 border-t border-white/20">
                  <h3 
                    className="text-white font-bold mb-3"
                    style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                  >
                    Sacred Presets
                  </h3>
                  <div className="grid grid-cols-1 gap-2">
                    <Button
                      onClick={() => {
                        setDimensions({ r: 1.618, theta: 144, phi: 72, t: 1.618, f: 528, u: 1.618, infinity: 3.14159 });
                      }}
                      variant="outline"
                      className="text-yellow-300 border-yellow-400 hover:bg-yellow-400/10"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      ✨ Golden Harmony
                    </Button>
                    
                    <Button
                      onClick={() => {
                        setDimensions({ r: 2.0, theta: 60, phi: 120, t: 1.0, f: 528, u: 1.0, infinity: 2.718 });
                      }}
                      variant="outline"
                      className="text-cyan-300 border-cyan-400 hover:bg-cyan-400/10"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      💙 Love Frequency
                    </Button>
                    
                    <Button
                      onClick={() => {
                        setDimensions({ r: 1.0, theta: 0, phi: 0, t: 0.5, f: 111, u: 0.618, infinity: 1.618 });
                      }}
                      variant="outline"
                      className="text-purple-300 border-purple-400 hover:bg-purple-400/10"
                      style={{ fontFamily: '"Crimson Text", "Libre Baskerville", serif' }}
                    >
                      🧘 Deep Meditation
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Instructions */}
        <div className="mt-8 text-center">
          <div className="bg-black/60 backdrop-blur-xl rounded-2xl p-6 border border-white/20">
            <p 
              className="text-white/95 text-lg leading-relaxed"
              style={{ 
                fontFamily: '"Crimson Text", "Libre Baskerville", serif',
                textShadow: '0 0 15px rgba(255, 255, 255, 0.8)'
              }}
            >
              Explore Grant's 7-dimensional mathematics through sound and sacred frequencies.<br/>
              Click the engine canvas to generate cosmic energy!
            </p>
          </div>
        </div>
      </div>
      
      <PersistentFooter />
    </div>
  );
};

// Enhanced Cosmic Audio Engine with Tibetan Bowl Harmonics
class CosmicAudioEngine {
  constructor() {
    this.audioContext = null;
    this.masterGain = null;
    this.oscillators = [];
    this.effects = {};
    this.isPlaying = false;
    this.dimensions = {
      r: 1.618, theta: 0, phi: 0, t: 1.0, f: 528, u: 1.0, infinity: 3.14159, tuvanThroat: 80
    };
    this.harmonicSeries = [];
    this.tuvanOscillators = []; // New: Tuvan throat singing oscillators
    this.persistentMode = false; // New: Persistence when navigating away
  }

  async init() {
    try {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
      this.masterGain = this.audioContext.createGain();
      this.masterGain.gain.value = 0.15; // Gentle volume
      this.masterGain.connect(this.audioContext.destination);
      this.setupTibetanBowlEffects();
      return true;
    } catch (error) {
      console.error('Cosmic audio initialization failed:', error);
      return false;
    }
  }

  setupTibetanBowlEffects() {
    // Enhanced Reverb for Sacred Space
    this.effects.reverb = this.audioContext.createConvolver();
    this.generateSacredReverb();
    this.effects.reverb.connect(this.masterGain);
    
    // Warm Low-Pass Filter for Organic Tibetan Sound
    this.effects.filter = this.audioContext.createBiquadFilter();
    this.effects.filter.type = 'lowpass';
    this.effects.filter.frequency.value = 3000;
    this.effects.filter.Q.value = 1.2;
    this.effects.filter.connect(this.effects.reverb);
    
    // Sacred Echo for Temple Acoustics
    this.effects.delay = this.audioContext.createDelay(3.0);
    this.effects.delay.delayTime.value = 0.8;
    this.effects.delayFeedback = this.audioContext.createGain();
    this.effects.delayFeedback.gain.value = 0.25;
    
    this.effects.delay.connect(this.effects.delayFeedback);
    this.effects.delayFeedback.connect(this.effects.delay);
    this.effects.delay.connect(this.effects.filter);
    
    // Harmonic Enhancer for Bowl Resonance
    this.effects.harmonicGain = this.audioContext.createGain();
    this.effects.harmonicGain.gain.value = 0.6;
    this.effects.harmonicGain.connect(this.effects.filter);
  }

  generateSacredReverb() {
    // Generate impulse response for temple/cave-like reverb
    const length = this.audioContext.sampleRate * 4; // 4 seconds
    const impulse = this.audioContext.createBuffer(2, length, this.audioContext.sampleRate);
    
    for (let channel = 0; channel < 2; channel++) {
      const channelData = impulse.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        const decay = Math.pow(1 - i / length, 1.2); // Gradual temple decay
        const earlyReflection = i < this.audioContext.sampleRate * 0.1 ? 
          Math.random() * 0.3 : 0;
        const lateReverb = Math.random() * 2 - 1;
        
        channelData[i] = (earlyReflection + lateReverb * decay) * decay;
      }
    }
    
    this.effects.reverb.buffer = impulse;
  }

  updateDimensions(newDimensions) {
    this.dimensions = { ...this.dimensions, ...newDimensions };
    if (this.isPlaying) {
      this.updateTibetanBowlParameters();
    }
  }

  async startCosmicSounds() {
    if (!this.audioContext) {
      const initialized = await this.init();
      if (!initialized) return false;
    }

    if (this.audioContext.state === 'suspended') {
      await this.audioContext.resume();
    }

    this.isPlaying = true;
    this.createTibetanBowlOscillators();
    return true;
  }

  stopCosmicSounds() {
    this.isPlaying = false;
    this.oscillators.forEach(osc => {
      if (osc.oscillator && osc.gainNode) {
        // Gentle fade out like Tibetan bowls
        osc.gainNode.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 2);
        setTimeout(() => {
          try {
            osc.oscillator.stop();
          } catch (e) {}
        }, 2000);
      }
    });
    this.oscillators = [];
  }

  createTibetanBowlOscillators() {
    this.stopCosmicSounds();
    this.isPlaying = true;
    this.oscillators = [];

    const baseFreq = this.dimensions.f;
    
    // FUNDAMENTAL - Deep Tibetan Bowl
    this.createBowlLayer('fundamental', baseFreq, 'sine', 0.25, {
      harmonics: [1, 1.5, 2.0, 2.5], // Lowered harmonics (was 2.1, 3.2, 4.8) for gentler sound
      beatFreq: 0.05, // Slow beat for meditation
      shimmer: 0.02
    });
    
    // HARMONIC SERIES - Traditional Bowl Overtones (LOWERED)
    this.createBowlLayer('harmonic_2nd', baseFreq * 1.5, 'sine', 0.15, { // Lowered from 2.1 to 1.5
      harmonics: [1, 1.3],
      beatFreq: 0.08,
      shimmer: 0.03
    });
    
    this.createBowlLayer('harmonic_3rd', baseFreq * 2.0, 'sine', 0.12, { // Lowered from 3.2 to 2.0
      harmonics: [1, 1.2],
      beatFreq: 0.12,
      shimmer: 0.04
    });
    
    // GOLDEN RATIO HARMONICS
    this.createBowlLayer('golden_fundamental', baseFreq * this.dimensions.r, 'sine', 0.18, {
      harmonics: [1, 1.618, 2.618],
      beatFreq: 0.06,
      shimmer: 0.025
    });
    
    // THETA & PHI MODULATED BOWLS (GENTLER FREQUENCIES)
    const thetaFreq = baseFreq * (1 + Math.sin(this.dimensions.theta * Math.PI / 180) * 0.1); // Reduced modulation
    this.createBowlLayer('theta_bowl', thetaFreq, 'sine', 0.14, {
      harmonics: [1, 1.8], // Lowered from 2.4
      beatFreq: 0.04,
      shimmer: 0.015
    });
    
    const phiFreq = baseFreq * (1 + Math.cos(this.dimensions.phi * Math.PI / 180) * 0.1); // Reduced modulation
    this.createBowlLayer('phi_bowl', phiFreq, 'sine', 0.16, {
      harmonics: [1, 1.6, 2.4], // Lowered from 1.8, 3.6
      beatFreq: 0.07,
      shimmer: 0.02
    });
    
    // DEEP MEDITATION DRONES - Sub-harmonic foundation
    this.createBowlLayer('deep_drone', baseFreq / 2, 'sine', 0.20, {
      harmonics: [1],
      beatFreq: 0.02,
      shimmer: 0.01
    });
    
    this.createBowlLayer('sub_drone', baseFreq / 4, 'sine', 0.10, {
      harmonics: [1],
      beatFreq: 0.01,
      shimmer: 0.005
    });
    
    // TIME FLOW MODULATED BOWLS
    const timeModulatedFreq = baseFreq * (0.8 + this.dimensions.t * 0.4);
    this.createBowlLayer('time_bowl', timeModulatedFreq, 'sine', 0.13, {
      harmonics: [1, 2.2],
      beatFreq: 0.03 * this.dimensions.t,
      shimmer: 0.02 * this.dimensions.t
    });
    
    // UNIVERSAL FIELD HARMONICS
    const universalFreq = baseFreq * this.dimensions.u;
    this.createBowlLayer('universal_bowl', universalFreq, 'sine', 0.12, {
      harmonics: [1, this.dimensions.u, this.dimensions.u * 2],
      beatFreq: 0.05,
      shimmer: 0.03
    });
    
    // NEW: ENHANCED TUVAN THROAT SINGING + HARMONIUM LAYER
    this.createTuvanThroatLayer(baseFreq, this.dimensions.tuvanThroat);
    this.createHarmoniumDroneLayer(baseFreq);
  }

  createTuvanThroatLayer(baseFreq, throatIntensity) {
    if (!this.isPlaying || !this.audioContext) return;
    
    // Enhanced Tuvan throat singing with multiple techniques
    const throatFundamental = Math.max(60, Math.min(120, throatIntensity));
    
    // PRIMARY THROAT DRONE (Kargyraa technique - deepest)
    const primaryThroat = this.audioContext.createOscillator();
    const primaryGain = this.audioContext.createGain();
    const primaryFilter = this.audioContext.createBiquadFilter();
    
    primaryThroat.type = 'sawtooth';
    primaryThroat.frequency.value = throatFundamental;
    
    primaryFilter.type = 'bandpass';
    primaryFilter.frequency.value = throatFundamental * 1.5;
    primaryFilter.Q.value = 12; // Very focused throat formant
    
    primaryGain.gain.value = 0.15; // Increased by 25% (was 0.12)
    
    // SECONDARY THROAT (Sygyt technique - whistle harmonics)
    const whistleThroat = this.audioContext.createOscillator();
    const whistleGain = this.audioContext.createGain();
    const whistleFilter = this.audioContext.createBiquadFilter();
    
    whistleThroat.type = 'sine';
    whistleThroat.frequency.value = throatFundamental * 4; // High harmonic whistle
    
    whistleFilter.type = 'bandpass';
    whistleFilter.frequency.value = throatFundamental * 4;
    whistleFilter.Q.value = 15; // Very sharp whistle
    
    whistleGain.gain.value = 0.05; // Increased by 25% (was 0.04)
    
    // CHÖÖMEI TECHNIQUE (Mid-range harmonic singing)
    const choomeiThroat = this.audioContext.createOscillator();
    const choomeiGain = this.audioContext.createGain();
    const choomeiFilter = this.audioContext.createBiquadFilter();
    
    choomeiThroat.type = 'square';
    choomeiThroat.frequency.value = throatFundamental * 2.5;
    
    choomeiFilter.type = 'bandpass';
    choomeiFilter.frequency.value = throatFundamental * 2.5;
    choomeiFilter.Q.value = 10;
    
    choomeiGain.gain.value = 0.075; // Increased by 25% (was 0.06)
    
    // Throat vibrato for all techniques
    const throatLFO = this.audioContext.createOscillator();
    const throatLFOGain = this.audioContext.createGain();
    
    throatLFO.type = 'sine';
    throatLFO.frequency.value = 5.5; // Throat vibrato rate
    throatLFOGain.gain.value = 2; // Subtle frequency modulation
    
    // Connect all throat techniques
    primaryThroat.connect(primaryFilter);
    primaryFilter.connect(primaryGain);
    primaryGain.connect(this.effects.filter);
    
    whistleThroat.connect(whistleFilter);
    whistleFilter.connect(whistleGain);
    whistleGain.connect(this.effects.filter);
    
    choomeiThroat.connect(choomeiFilter);
    choomeiFilter.connect(choomeiGain);
    choomeiGain.connect(this.effects.filter);
    
    throatLFO.connect(throatLFOGain);
    throatLFOGain.connect(primaryThroat.frequency);
    throatLFOGain.connect(choomeiThroat.frequency);
    
    // Start all throat singing techniques
    primaryThroat.start();
    whistleThroat.start();
    choomeiThroat.start();
    throatLFO.start();
    
    // Store for cleanup
    this.tuvanOscillators.push(
      { osc: primaryThroat, gain: primaryGain, lfo: throatLFO, type: 'kargyraa' },
      { osc: whistleThroat, gain: whistleGain, type: 'sygyt' },
      { osc: choomeiThroat, gain: choomeiGain, type: 'choomei' }
    );
  }

  createHarmoniumDroneLayer(baseFreq) {
    if (!this.isPlaying || !this.audioContext) return;
    
    // HARMONIUM DRONE LAYER (Indian classical drone)
    const harmoniumFundamental = baseFreq / 2; // Lower octave for drone base
    
    // Primary drone (Sa - tonic)
    const harmoniumDrone1 = this.audioContext.createOscillator();
    const harmoniumGain1 = this.audioContext.createGain();
    const harmoniumFilter1 = this.audioContext.createBiquadFilter();
    
    harmoniumDrone1.type = 'square'; // Reedy harmonium sound
    harmoniumDrone1.frequency.value = harmoniumFundamental;
    
    harmoniumFilter1.type = 'lowpass';
    harmoniumFilter1.frequency.value = harmoniumFundamental * 3;
    harmoniumFilter1.Q.value = 2;
    
    harmoniumGain1.gain.value = 0.08;
    
    // Fifth drone (Pa - perfect fifth)
    const harmoniumDrone2 = this.audioContext.createOscillator();
    const harmoniumGain2 = this.audioContext.createGain();
    const harmoniumFilter2 = this.audioContext.createBiquadFilter();
    
    harmoniumDrone2.type = 'sawtooth';
    harmoniumDrone2.frequency.value = harmoniumFundamental * 1.5; // Perfect fifth
    
    harmoniumFilter2.type = 'lowpass';
    harmoniumFilter2.frequency.value = harmoniumFundamental * 4;
    harmoniumFilter2.Q.value = 2;
    
    harmoniumGain2.gain.value = 0.06;
    
    // Octave drone (higher Sa)
    const harmoniumDrone3 = this.audioContext.createOscillator();
    const harmoniumGain3 = this.audioContext.createGain();
    const harmoniumFilter3 = this.audioContext.createBiquadFilter();
    
    harmoniumDrone3.type = 'square';
    harmoniumDrone3.frequency.value = harmoniumFundamental * 2; // Octave
    
    harmoniumFilter3.type = 'lowpass';
    harmoniumFilter3.frequency.value = harmoniumFundamental * 5;
    harmoniumFilter3.Q.value = 1.5;
    
    harmoniumGain3.gain.value = 0.05;
    
    // Harmonium bellows breathing effect
    const harmoniumLFO = this.audioContext.createOscillator();
    const harmoniumLFOGain = this.audioContext.createGain();
    
    harmoniumLFO.type = 'sine';
    harmoniumLFO.frequency.value = 0.3; // Slow breathing rate
    harmoniumLFOGain.gain.value = 0.02; // Subtle volume breathing
    
    // Connect harmonium drones
    harmoniumDrone1.connect(harmoniumFilter1);
    harmoniumFilter1.connect(harmoniumGain1);
    harmoniumGain1.connect(this.effects.filter);
    
    harmoniumDrone2.connect(harmoniumFilter2);
    harmoniumFilter2.connect(harmoniumGain2);
    harmoniumGain2.connect(this.effects.filter);
    
    harmoniumDrone3.connect(harmoniumFilter3);
    harmoniumFilter3.connect(harmoniumGain3);
    harmoniumGain3.connect(this.effects.filter);
    
    // Breathing effect
    harmoniumLFO.connect(harmoniumLFOGain);
    harmoniumLFOGain.connect(harmoniumGain1.gain);
    harmoniumLFOGain.connect(harmoniumGain2.gain);
    
    // Start harmonium drones
    harmoniumDrone1.start();
    harmoniumDrone2.start();
    harmoniumDrone3.start();
    harmoniumLFO.start();
    
    // Store for cleanup and persistence
    this.tuvanOscillators.push(
      { osc: harmoniumDrone1, gain: harmoniumGain1, type: 'harmonium_sa' },
      { osc: harmoniumDrone2, gain: harmoniumGain2, type: 'harmonium_pa' },
      { osc: harmoniumDrone3, gain: harmoniumGain3, lfo: harmoniumLFO, type: 'harmonium_sa_octave' }
    );
  }

  createBowlLayer(name, fundamentalFreq, waveType, volume, config) {
    if (!this.isPlaying || !this.audioContext) return;

    const bowlGroup = {
      name,
      oscillators: [],
      gainNodes: [],
      lfoNodes: [],
      fundamentalFreq,
      config
    };

    // Create harmonic series for this bowl
    config.harmonics.forEach((harmonic, index) => {
      const frequency = fundamentalFreq * harmonic;
      
      // Oscillator for this harmonic
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      // LFO for organic beating/shimmer
      const lfo = this.audioContext.createOscillator();
      const lfoGain = this.audioContext.createGain();
      
      oscillator.type = waveType;
      oscillator.frequency.value = Math.max(20, Math.min(4000, frequency));
      
      // Tibetan bowl-style LFO (very slow, organic)
      lfo.type = 'sine';
      lfo.frequency.value = config.beatFreq + (index * 0.01);
      lfoGain.gain.value = frequency * config.shimmer;
      
      lfo.connect(lfoGain);
      lfoGain.connect(oscillator.frequency);
      
      // Volume envelope for each harmonic (quieter harmonics)
      const harmonicVolume = volume / (index + 1); // Natural harmonic decay
      gainNode.gain.value = harmonicVolume;
      
      // Gentle attack like striking a bowl
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(harmonicVolume, this.audioContext.currentTime + 0.5);
      
      oscillator.connect(gainNode);
      gainNode.connect(this.effects.harmonicGain);

      oscillator.start();
      lfo.start();

      bowlGroup.oscillators.push(oscillator);
      bowlGroup.gainNodes.push(gainNode);
      bowlGroup.lfoNodes.push(lfo);
    });

    this.oscillators.push(bowlGroup);
  }

  updateTibetanBowlParameters() {
    if (!this.isPlaying || this.oscillators.length === 0) return;

    this.oscillators.forEach(bowlGroup => {
      const newFundamental = this.calculateNewFrequency(bowlGroup.name, bowlGroup.fundamentalFreq);
      
      bowlGroup.oscillators.forEach((oscillator, index) => {
        if (!oscillator) return;
        
        const harmonic = bowlGroup.config.harmonics[index];
        const newFreq = newFundamental * harmonic;
        const boundedFreq = Math.max(20, Math.min(4000, newFreq));
        
        try {
          // Smooth frequency transitions like real bowls
          oscillator.frequency.exponentialRampToValueAtTime(
            boundedFreq, 
            this.audioContext.currentTime + 0.5
          );
        } catch (error) {
          // Oscillator might have been stopped
        }
      });
      
      // Update LFO speeds for organic beating
      bowlGroup.lfoNodes.forEach((lfo, index) => {
        if (!lfo) return;
        
        try {
          const newLfoSpeed = bowlGroup.config.beatFreq * (1 + this.dimensions.t * 0.3) + (index * 0.01);
          lfo.frequency.exponentialRampToValueAtTime(
            Math.max(0.01, Math.min(2, newLfoSpeed)),
            this.audioContext.currentTime + 1
          );
        } catch (error) {
          // LFO might have been stopped
        }
      });
    });

    // Update effects for spatial enhancement
    const harmony = this.calculateDimensionalHarmony();
    const filterFreq = 1500 + harmony * 25; // Keep it warm and mellow
    
    try {
      this.effects.filter.frequency.exponentialRampToValueAtTime(
        filterFreq, 
        this.audioContext.currentTime + 1
      );
      
      const delayTime = 0.4 + (this.dimensions.t / 5) * 1.0; // Sacred echo timing
      this.effects.delay.delayTime.setValueAtTime(
        Math.max(0.1, Math.min(2.5, delayTime)), 
        this.audioContext.currentTime
      );
    } catch (error) {
      // Effects might not be available
    }
  }

  calculateNewFrequency(bowlName, originalFreq) {
    const baseFreq = this.dimensions.f;
    
    switch (bowlName) {
      case 'fundamental':
        return baseFreq;
      case 'harmonic_2nd':
        return baseFreq * 2.1;
      case 'harmonic_3rd':
        return baseFreq * 3.2;
      case 'golden_fundamental':
        return baseFreq * this.dimensions.r;
      case 'theta_bowl':
        return baseFreq * (1 + Math.sin(this.dimensions.theta * Math.PI / 180) * 0.2);
      case 'phi_bowl':
        return baseFreq * (1 + Math.cos(this.dimensions.phi * Math.PI / 180) * 0.15);
      case 'deep_drone':
        return baseFreq / 2;
      case 'sub_drone':
        return baseFreq / 4;
      case 'time_bowl':
        return baseFreq * (0.8 + this.dimensions.t * 0.4);
      case 'universal_bowl':
        return baseFreq * this.dimensions.u;
      default:
        return originalFreq;
    }
  }

  calculateDimensionalHarmony() {
    // Mathematical harmony based on sacred ratios
    const goldenRatioProximity = 1 - Math.abs(this.dimensions.r - 1.618) / 5;
    const sacredFreqProximity = 1 - Math.abs(this.dimensions.f - 528) / 500;
    const piProximity = 1 - Math.abs(this.dimensions.infinity - 3.14159) / 3;
    const angleHarmony = (Math.sin(this.dimensions.theta * Math.PI / 180) + Math.cos(this.dimensions.phi * Math.PI / 180)) / 2;
    
    return Math.max(0, Math.min(100, 
      (goldenRatioProximity + sacredFreqProximity + piProximity + angleHarmony + this.dimensions.t + this.dimensions.u) * 12
    ));
  }

  playFormulaDiscoverySound(formulaName) {
    if (!this.audioContext) return;

    // Beautiful discovery chime like temple bells - LOWERED BY HALF for gentler tone
    const discoveryFreqs = [264, 330, 412.5]; // Sacred interval (halved from 528, 660, 825)
    
    discoveryFreqs.forEach((freq, index) => {
      setTimeout(() => {
        const bell = this.audioContext.createOscillator();
        const bellGain = this.audioContext.createGain();
        const bellFilter = this.audioContext.createBiquadFilter();
        
        bell.type = 'sine';
        bell.frequency.value = freq;
        
        bellFilter.type = 'bandpass';
        bellFilter.frequency.value = freq;
        bellFilter.Q.value = 8; // Sharp resonance like real bowl
        
        bellGain.gain.value = 0;
        bellGain.gain.exponentialRampToValueAtTime(0.25, this.audioContext.currentTime + 0.1); // Slightly reduced volume
        bellGain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 4);
        
        bell.connect(bellFilter);
        bellFilter.connect(bellGain);
        bellGain.connect(this.effects.reverb);
        
        bell.start();
        bell.stop(this.audioContext.currentTime + 4);
        
        console.log(`🎵 SACRED DISCOVERY CHIME: ${freq}Hz (lowered) - ${formulaName}`);
      }, index * 300);
    });
  }

  setMasterVolume(volume) {
    if (this.masterGain) {
      this.masterGain.gain.exponentialRampToValueAtTime(
        Math.max(0.001, Math.min(0.3, volume)), 
        this.audioContext.currentTime + 0.5
      );
    }
  }

  // Enhanced persistence functionality with heartbeat integration
  enablePersistentMode() {
    this.persistentMode = true;
    
    // Store sonic creation in global state for site-wide persistence
    window.planetaryFedSonicExperience = {
      enabled: true,
      dimensions: { ...this.dimensions },
      audioEngine: this,
      isPlaying: this.isPlaying,
      heartbeatIntegration: true
    };
    
    // Integrate with global heartbeat system
    if (window.planetaryFedHeartbeat) {
      window.planetaryFedHeartbeat.addSonicLayer(this);
    }
    
    console.log('🎵 SONIC PERSISTENCE: Sound creation now persistent across entire site');
    console.log('💗 HEARTBEAT INTEGRATION: Sonic experience synchronized with heartbeat');
  }

  disablePersistentMode() {
    this.persistentMode = false;
    
    // Remove from global state
    if (window.planetaryFedSonicExperience) {
      delete window.planetaryFedSonicExperience;
    }
    
    // Remove from heartbeat integration
    if (window.planetaryFedHeartbeat) {
      window.planetaryFedHeartbeat.removeSonicLayer();
    }
    
    console.log('🎵 SONIC PERSISTENCE: Disabled - audio local to sonic experience page only');
  }

  // Enhanced for global mute via heart toggle
  setGlobalMute(muted) {
    if (this.masterGain) {
      if (muted) {
        this.masterGain.gain.exponentialRampToValueAtTime(0.001, this.audioContext.currentTime + 0.3);
        console.log('🤫 SONIC EXPERIENCE: Muted by universal heart toggle');
      } else {
        this.masterGain.gain.exponentialRampToValueAtTime(0.15, this.audioContext.currentTime + 0.3);
        console.log('🎵 SONIC EXPERIENCE: Unmuted by universal heart toggle');
      }
    }
  }

  // Enhanced updateDimensions to include Tuvan throat
  updateDimensions(newDimensions) {
    this.dimensions = { ...this.dimensions, ...newDimensions };
    
    // Update Tuvan throat singing intensity
    if (this.tuvanOscillators.length > 0 && newDimensions.tuvanThroat !== undefined) {
      this.tuvanOscillators.forEach((tuvanElement, index) => {
        if (tuvanElement.osc && tuvanElement.osc.frequency) {
          const newThroatFreq = Math.max(60, Math.min(120, newDimensions.tuvanThroat));
          tuvanElement.osc.frequency.exponentialRampToValueAtTime(
            newThroatFreq * (index === 0 ? 1 : index + 1), // Harmonics for overtones
            this.audioContext.currentTime + 0.5
          );
        }
      });
    }
    
    // Update persistence if enabled
    if (this.persistentMode && window.planetaryFedSonicExperience) {
      window.planetaryFedSonicExperience.dimensions = { ...this.dimensions };
    }
  }

  destroy() {
    // Clean up Tuvan oscillators
    this.tuvanOscillators.forEach(element => {
      try {
        if (element.osc) element.osc.stop();
        if (element.lfo) element.lfo.stop();
      } catch (e) {
        console.log('Tuvan oscillator already stopped');
      }
    });
    this.tuvanOscillators = [];
    
    this.stopCosmicSounds();
    
    // Only destroy audio context if not in persistent mode
    if (!this.persistentMode && this.audioContext && this.audioContext.state !== 'closed') {
      this.audioContext.close();
    }
  }
}

export default SonicExperience;